// SQ-AI — CEO-facing conversational page for the chart-mode agent.
//
// Single entry point for the lab (per CLAUDE.md). The route /sq-ai mounts this
// component; everything else under labs/sq-ai/ is internal.
//
// Phase-2: this page DOGFOODS the SDK. The socket + conversation state machine no
// longer live here — they come from @safetyqube/sq-ai-react (SqAiProvider +
// useSqAi), exactly the way SafetyQube will consume the SDK. The page keeps its
// own pixels (Thread / Composer / the ChartRenderer) and the context picker
// (usePicker stays app-local — it's HTTP/service-coupled, a separate concern).
//
// Structure: the outer SqAiPage owns scope state (env / tenant / confidential /
// tz), conversationId, the picker, the saved-chat listing, and the tenant/terminology
// fetches, and feeds them into <SqAiProvider>. The inner SqAiConsole consumes
// useSqAi() and renders.
//
// SAVED CHATS ARE A SECOND, READ-ONLY SURFACE, not a way into the live one — WHILE CONTINUING CHATS IS OFF. The rail lists what the
// broker consumer persisted (useChatHistory) and opening one replaces the thread with a transcript;
// the SDK's conversation is untouched underneath and "Back to chat" returns to it.
// CONTINUING CHATS ON — the server says so (`resumeChats`, read with the picker's modules; design v2 D20) — a saved chat of the
// caller's own, opened from the rail or a reload of its link, CONTINUES (RSM4, Round 3): it becomes the live conversation, its saved
// turns drawn above the live thread, its chips restored (D12) and its mode shown, the text box back. The server reads it back
// (ai:open_chat → ai:resumed) and says the rest: D18's one line where the day has changed, the countdown from 50 questions left
// (D4), "Continue here?" where another tab holds it (D14), "moved" to the tab that lost it, and a full chat — each drawn below.
// The live chat's link goes in the address once it has a message, so a reload continues it too (RSM9: with continuing off as well, where a
// reload opens the link's read-only view; it names the chat even if its first question failed — a reload then reads "isn't available").
// ("MINE ONLY", D21, is not behind that switch: the internal page always has the box, and another user's chat — listed by it, or
// reached by its link — opens read-only, saying whose it is, whether continuing is on or off.)
// A PASSING PROBLEM NEVER ENDS A CHAT (RSM6 — the developer's decisions of 7 Oct for the web app, P4 / P5 / Q2 / R3-3b, the same here):
// with continuing on, a dropped connection, socket.io still trying or given up, or an open refused for any reason the resume rules do
// not own draws a LOADER on the chat — never "SQ-AI is unavailable", "This chat has ended" or a read-only chat — and when the connection
// is back the chat on screen is opened again and carries on, the reader's chips kept. Until the server has said whether continuing is
// on, the chat area is only a loader (on or off).
// Context carries across turns on one conversationId; changing env / tenant /
// confidential access / timezone starts a new chat because context across scopes
// makes no sense.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, History, LogOut, MessageSquarePlus, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SqAiProvider, useSqAi } from "@safetyqube/sq-ai-react";
import { tenantApi, aiAssistantApi } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { usePicker } from "./usePicker";
import { useChatHistory } from "./useChatHistory";
import useMessageTimes from "./useMessageTimes";
import styles from "./sqAi.module.css";
import Composer from "./components/Composer";
import ContextPicker from "./components/ContextPicker";
import Thread from "./components/Thread";
import EmptyState from "./components/EmptyState";
import SettingsBar, { ENVS } from "./components/SettingsBar";
import HistoryRail, { ownerLabel } from "./components/HistoryRail";
import BillingDebugPanel from "./components/BillingDebugPanel";
import HistoryTranscript from "./components/HistoryTranscript";

function newConversationId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "sq-" + Date.now() + "-" + Math.floor(Math.random() * 1000000);
}

// The address names the chat selected in the rail (?env=<env>&tenant=<schema>&c=<id>) so the link
// reopens it; null clears all three. The env and tenant ride with the id because nothing on this page
// is remembered between tabs and the saved-chat read is scoped to a tenant — a bare id opened in a new
// tab would be looked up under the default env's first tenant. replaceState, not a navigation:
// selecting a chat is not a page the back button should step through, and the router's own entry
// state is kept as it is.
function setAddressConversation(id, env, tenantSchema) {
  const url = new URL(window.location.href);
  for (const [key, value] of [["env", env], ["tenant", tenantSchema], ["c", id]]) {
    if (id && value) url.searchParams.set(key, value);
    else url.searchParams.delete(key);
  }
  window.history.replaceState(window.history.state, "", url);
}

// The env and tenant a ?c= link names, read once when the page loads. An env the page does not
// offer is ignored; a tenant is kept only if the env's tenant list holds it (the list load decides).
function readAddressScope() {
  const params = new URLSearchParams(window.location.search);
  if (!params.get("c")) return { env: null, tenant: null };
  const env = params.get("env");
  return { env: ENVS.includes(env) ? env : null, tenant: params.get("tenant") || null };
}

// A continued chat's state on this page (RSM4): opening (ai:open_chat sent, ai:resumed not yet back — the text box waits), asking
// "Continue here?" (D14; `takeOver` once answered yes), `blocked` — the server's sentence where the text box has gone (moved to
// another tab, full, no longer readable) or this tab's own read-only answer (no) — D18's `notice`, and the countdown's `limitText`.
// `continued` marks a chat taken up from the store or the list (not one started on this page), so going back to it shows its history
// again — as it stood once anything is asked here, read again while nothing is (takenRef).
const RESUME_IDLE = Object.freeze({ id: null, continued: false, opening: false, asking: false, takeOver: false, blocked: null, notice: null, limitText: null,
  ...PASSING_IDLE() });

// …and what a passing problem leaves on it (RSM6): `reopen` — the open is the chat opened AGAIN after a dropped connection or a lost
// question, so it restores no chips over the reader's; `tries` / `retryWait` — an open refused for a passing reason, sent again after
// OPEN_RETRY_MS; `pending` — a question to send once the chat is open here (a lost question re-sent, Q2's question after "Yes");
// `hide` — the turns the SDK drew for a question the server refused that way, not drawn while it waits; `stamps` — what an answer
// showed before the SDK stamped an open's refusal on it; `cuts` — every answer a dropped connection cut off, each kept settled (RSM6 review 1 B2:
// the SDK leaves each one in flight, so a later drop must not set an earlier one spinning again).
function PASSING_IDLE() {
  return { reopen: false, tries: 0, retryWait: false, pending: null, hide: [], stamps: {}, cuts: [] };
}
// An open refused for a passing reason is sent again after each of these, then the page stops waiting: the text box simply there,
// nothing said, a refused question put back in it (the web app's own choice, RSM6 mirrors it).
const OPEN_RETRY_MS = [1000, 2000, 4000, 8000, 16000];
// Where socket.io has given up (five failed attempts, or the server closed the connection) the page tries again this often, and at
// once when the browser says it is back online.
const REVIVE_EVERY_MS = 15000;

// The resume state for chat `id`: the one held, or a fresh one for it.
function _resumeFor(r, id) {
  return r.id === id ? r : { ...RESUME_IDLE, id };
}

// The thread as the reader sees it with continuing on (RSM6): a question the server refused because the chat was lost or open in
// another tab is not drawn while it waits to be sent (it is drawn once, when it is); an answer the SDK stamped with an open's refusal
// shows what it showed before; the answer a dropped connection cut off is settled — its spinner stops, the server ended its turn with
// the connection.
function _shownMessages(messages, r) {
  return messages.filter((m) => !r.hide.includes(m.id)).map((m) => {
    const was = Object.prototype.hasOwnProperty.call(r.stamps, m.id) ? { ...m, ...r.stamps[m.id] } : m;
    if (!r.cuts.includes(was.id) || was.done) return was;
    return { ...was, done: true, thinkingSteps: (was.thinkingSteps || []).map((t) => (t.status === "live" ? { ...t, status: "done" } : t)) };
  });
}

// The conversation surface — consumes the SDK via useSqAi(). Scope state, the
// picker, and the fetched tenant data are owned by SqAiPage (above the provider)
// and passed in as props.
function SqAiConsole({
  mode,
  setMode,
  env,
  tenants,
  tenantSchema,
  tenantsLoading,
  tenantsError,
  tenantsReady,
  confidentialAccess,
  webSearch,
  userTz,
  conversationId,
  settingsOpen,
  setSettingsOpen,
  notice,
  setNotice,
  terms,
  picker,
  history,
  setEnv,
  setTenantSchema,
  setConfidentialAccess,
  setWebSearch,
  setUserTz,
  startNewChat,
  continueChat,
  mineOnly,
  setMineOnly,
}) {
  const { state, send, cancel, connected, connectionError, socket, sessionPosture } = useSqAi();
  const { logout } = useAuth();
  const navigate = useNavigate();
  // The scrolling element — Thread reads it to decide whether the reader is still
  // following the stream (stick-to-bottom), so it's owned where it's rendered.
  const scrollRef = useRef(null);

  // A saved chat is showing INSTEAD of the live thread, not beside it — one main pane, and the
  // live conversation keeps streaming into the SDK's state while it is hidden.
  const viewingSaved = history.viewingId !== null && history.viewingId !== conversationId;
  const [railOpen, setRailOpen] = useState(true);
  // CONTINUING CHATS (RSM4) — only where the server says it is on; off, none of what follows draws or sends anything.
  const resumeOn = picker.resumeChats === true;
  // A suspended account's warning arrives with the picker, so the internal page shows it before the first turn. ACCESS-54
  useEffect(() => {
    if (picker.accountNotice) setNotice(picker.accountNotice);
  }, [picker.accountNotice, setNotice]);
  // The saved chat being continued: its transcript is the history above the live thread of the same id.
  const continuing = resumeOn && history.viewingId !== null && history.viewingId === conversationId;
  const [resume, setResume] = useState(RESUME_IDLE);
  const resumeHere = resumeOn && resume.id === conversationId;
  // (the thread less what a passing problem left on it — _shownMessages; off, the SDK's thread as it is)
  const messages = resumeHere ? _shownMessages(state.messages, resume) : state.messages;
  // When each message was sent or finished (see useMessageTimes), drawn in `userTz` — the Settings
  // timezone this page also hands the engine.
  const times = useMessageTimes(messages);
  const lastMessage = state.messages[state.messages.length - 1];
  const hasMessages = messages.length > 0;
  const isInFlight = state.status === "in_flight" && !(resumeHere && lastMessage && resume.cuts.includes(lastMessage.id));
  // The question the server is answering now, as the SDK drew it — the one an ai:error refuses (RSM6).
  const beforeLast = state.messages[state.messages.length - 2];
  const asked = isInFlight && lastMessage && lastMessage.role === "assistant" && beforeLast && beforeLast.role === "user"
    ? { text: beforeLast.text, ids: [beforeLast.id, lastMessage.id] } : null;
  // Whether this chat has had an answer here — a chat started on this page with none may never have been saved (R3-3b).
  const answeredHere = messages.some((m) => m.role === "assistant" && m.done && !m.error && !resume.cuts.includes(m.id));
  // A PASSING PROBLEM (RSM6, P4): the connection lost after it was up, or socket.io failing to reach the server at all — a refusal the
  // engine names (an expired session, a refused sign-in) is not one, and keeps today's screen.
  const transportDown = Boolean(connectionError && connectionError.code === "connect_error");
  const everConnectedRef = useRef(false);
  const connectionLost = resumeOn && !connected && (everConnectedRef.current || transportDown);
  const waitingHere = resumeHere && (resume.retryWait || (resume.opening && resume.reopen));
  // A question handed back to the reader, put in the text box once it is on screen (Composer.putBack).
  const composerRef = useRef(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (draft === null || !composerRef.current) return;
    composerRef.current.putBack(draft);
    setDraft(null);
  });

  // A link carrying ?c=<id> opens that chat once the tenant list has settled the link's env and
  // tenant, through the same read the rail uses — so it opens only what the signed-in user could
  // already open from the rail. A tenant the env does not list has been replaced by then, and the
  // read under the replacement answers "not available" rather than showing another tenant's chat.
  // (…once the server has also said whether continuing is on: on, the read asks whose the chat is, and the caller's own continues)
  const linkedIdRef = useRef(new URLSearchParams(window.location.search).get("c"));
  useEffect(() => {
    const id = linkedIdRef.current;
    if (!id || tenantsLoading || !tenantSchema || picker.resumeChats === null) return;
    linkedIdRef.current = null;
    history.open(id, "", { everyone: true });
  }, [tenantsLoading, tenantSchema, history, picker.resumeChats]);

  // ── Continuing a saved chat (RSM4) ──
  // A saved chat the server says is the caller's own becomes the live conversation — not while a turn runs (its answer would land
  // in a chat no longer on screen): the address names it, and the open below asks the server to read it back.
  useEffect(() => {
    const id = history.viewingId;
    if (!resumeOn || isInFlight || !id || id === conversationId || !history.viewOwner || history.viewOwner.self !== true) return;
    setAddressConversation(id, env, tenantSchema);
    setResume({ ...RESUME_IDLE, id, continued: true, opening: true });
    continueChat(id);
  }, [resumeOn, isInFlight, history.viewingId, history.viewOwner, conversationId, continueChat, env, tenantSchema]);
  // The continued chat's history as it stands above the live thread — the last read of it (taken up with, or again while nothing was
  // asked here), and any earlier pages scrolled in —
  // kept in ONE slot, so going back to the chat from another one shows exactly that again once anything has been asked here (reviews
  // R4 → 8): the store by then also holds the turns asked here, which the live thread shows, and the page holds no turn ids to tell them
  // apart (every rule that guessed — by time, by words — drew a turn twice or hid one, reviews 4–8). While NOTHING has been asked here the
  // live thread is empty and the store is read again — going back, and once a take-over is answered — so a turn stored late, or another
  // tab's, appears. ⚠ Not closed: a turn stored only after the chat was taken up (the store lags its turn by about two seconds) is missing
  // above, once this tab has asked something, until a reload.
  const takenRef = useRef(null);
  useEffect(() => {
    if (continuing && !history.viewLoading && !history.viewError) takenRef.current = history.snapshot();
  }, [continuing, history]);
  // Another conversation on screen leaves the continued one's state behind.
  useEffect(() => {
    setResume((r) => (r.id === conversationId ? r : RESUME_IDLE));
  }, [conversationId]);
  // The open — and, once "Continue here?" is answered yes, the take-over (D14).
  // Sent while connected, and again on a reconnect while still opening: a connection that dropped took the open's answer with it, and the
  // server forgot what that connection held (review 3).
  const reopenHeld = resume.reopen && viewingSaved;
  // The open sent on this connection and not yet answered — never sent twice: a reader looking at another chat and coming back re-ran
  // this effect and sent it again, and a take-over's second send, refused as in flight, left "Continue here?" up over a chat already
  // taken (RSM6 review 2). Cleared by its answer — ai:resumed or ai:error for THAT chat, whichever chat is on screen when it lands, so a
  // reader who left and comes back is not left waiting on an open never sent (review 3); never by ai:chat_moved or ai:chat_limit, which
  // answer no open (review 4) — and by a dropped connection, which took it. ⚠ Not closed: an answer names its chat, not which open it
  // answers, so a late answer to an earlier open of the same chat clears a newer one (a request id on the open and its answer — an
  // event change — would close it).
  const openSentRef = useRef(null);
  useEffect(() => {
    // (…and not under the previous env's account while the new env's list is still on its way — RSM5 review 1)
    if (!socket || !connected || !resume.opening || resume.id !== conversationId || !tenantSchema || !tenantsReady) return;
    // (…and a chat opened AGAIN only while it is the one on screen — a reader who opened another saved chat meanwhile is not pulled
    // back; going back to it opens it then: RSM6. Keyed on the two together, so looking at another chat while a FIRST open is out
    // sends it no second time: review 1 B5)
    if (reopenHeld) return;
    const sent = `${conversationId}|${resume.takeOver ? "takeOver" : "open"}`;
    if (openSentRef.current === sent) return;
    openSentRef.current = sent;
    socket.emit("ai:open_chat", { conversationId, tenantSchema, ...(resume.takeOver ? { takeOver: true } : {}) });
  }, [socket, connected, resume.opening, resume.takeOver, resume.id, conversationId, tenantSchema, tenantsReady, reopenHeld]);
  // ── A passing problem (RSM6 — P4, Q2, R3-3b) ──
  // When the connection comes back the chat on screen is opened AGAIN — the server forgot what the dropped connection held: a chat
  // continued here, or one started here that has sent anything. Not one already opening (the open above is sent again by itself), one
  // waiting on "Continue here?", or one this tab can no longer write to. A first open waiting to be sent again stays a FIRST open, so
  // its answer still restores the chat's chips and mode (review 1 B1) — and goes at once, as its retry would. With another saved chat on
  // screen a RE-open is marked all the same and waits until the reader goes back to it (review 1 B3; review 2).
  const wasConnectedRef = useRef(connected);
  useEffect(() => {
    const was = wasConnectedRef.current;
    const before = everConnectedRef.current;
    wasConnectedRef.current = connected;
    if (connected) everConnectedRef.current = true;
    if (!connected || was || !before || !resumeOn) return;
    if (!continuing && !(resumeHere && resume.continued) && state.messages.length === 0) return;
    setResume((r) => {
      const here = _resumeFor(r, conversationId);
      if (here.opening || here.asking || here.blocked) return here;
      return { ...here, opening: true, reopen: here.retryWait ? here.reopen : true, tries: 0, retryWait: false };
    });
    // eslint-disable-next-line
  }, [connected]);
  // An open refused for a passing reason waits, then is sent again.
  useEffect(() => {
    if (!resumeHere || !resume.retryWait) return undefined;
    const wait = setTimeout(() => {
      setResume((r) => (r.id === conversationId && r.retryWait ? { ...r, retryWait: false, opening: true } : r));
    }, OPEN_RETRY_MS[Math.min(resume.tries, OPEN_RETRY_MS.length) - 1]);
    return () => clearTimeout(wait);
  }, [resumeHere, resume.retryWait, resume.tries, conversationId]);
  // A question waiting for the chat to be open here is sent once it is — once (a lost question re-sent; Q2's after "Yes").
  useEffect(() => {
    if (!resumeHere || resume.pending === null || resume.opening || resume.retryWait || resume.asking || resume.blocked) return;
    if (!connected || isInFlight || !tenantSchema || !tenantsReady) return;
    const question = resume.pending;
    setResume((r) => ({ ...r, pending: null }));
    setNotice(null);
    send(question);
  }, [resumeHere, resume.pending, resume.opening, resume.retryWait, resume.asking, resume.blocked, connected, isInFlight, tenantSchema, tenantsReady, send, setNotice]);
  // Where socket.io has given up — five failed attempts, or the server closed the connection — nothing retries by itself: the page
  // does, on the browser's `online` and every REVIVE_EVERY_MS.
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!socket) return undefined;
    const onFailed = () => setGaveUp(true);
    const onClosed = (reason) => {
      if (reason === "io server disconnect") setGaveUp(true);
    };
    const onBack = () => setGaveUp(false);
    const manager = socket.io;
    if (manager && manager.on) manager.on("reconnect_failed", onFailed);
    socket.on("disconnect", onClosed);
    socket.on("connect", onBack);
    return () => {
      if (manager && manager.off) manager.off("reconnect_failed", onFailed);
      socket.off("disconnect", onClosed);
      socket.off("connect", onBack);
    };
  }, [socket]);
  useEffect(() => {
    if (!resumeOn || !gaveUp || connected || !socket) return undefined;
    const revive = () => {
      if (!socket.connected) socket.connect();
    };
    window.addEventListener("online", revive);
    const every = setInterval(revive, REVIVE_EVERY_MS);
    return () => {
      window.removeEventListener("online", revive);
      clearInterval(every);
    };
  }, [resumeOn, gaveUp, connected, socket]);
  // The live chat's link goes in the address once it has a message, so a reload opens it (continuing on: D18, it continues; off: the
  // link's read-only view) — never over another chat's, and
  // only once the thread on screen IS this chat's: on a New chat or a scope change the id moves a render before the SDK empties the old
  // chat's thread, and the address then named the new, empty chat, which a reload answered "isn't available" (measured on the server).
  useEffect(() => {
    if (hasMessages && state.id === conversationId && (history.viewingId === null || history.viewingId === conversationId)) setAddressConversation(conversationId, env, tenantSchema);
  }, [hasMessages, state.id, history.viewingId, conversationId, env, tenantSchema]);
  // What the server says of the chat on screen. A ref keeps the listeners stable across renders, as the picker's own does below.
  const resumeCtxRef = useRef(null);
  resumeCtxRef.current = { resumeOn, conversationId, picker, setMode, history, startNewChat, opening: resumeHere && resume.opening, takingOver: resumeHere && resume.opening && resume.takeOver,
    liveEmpty: !hasMessages, resume: resumeHere ? resume : _resumeFor(resume, conversationId), asked, viewingSaved, setDraft,
    inFlight: state.status === "in_flight", lastId: lastMessage ? lastMessage.id : null,
    lastAnswer: [...state.messages].reverse().find((m) => m.role === "assistant") || null,
    lastAsked: ([...state.messages].reverse().find((m) => m.role === "user") || {}).text || null,
    // (a chat that is this reader's for certain: one answered here, or one continued here — past its first open, whose refusal is
    // today's; a chat started here with no answer may never have been saved: R3-3b)
    ours: answeredHere || (resumeHere && resume.continued && (resume.reopen || !resume.opening)),
    unansweredHere: !(resumeHere && resume.continued) && !continuing && state.messages.length > 0 && !answeredHere };
  useEffect(() => {
    if (!socket) return undefined;
    const mine = (p, answersOpen) => {
      const cur = resumeCtxRef.current;
      if (answersOpen && p && openSentRef.current && openSentRef.current.startsWith(`${p.conversationId}|`)) openSentRef.current = null;
      return Boolean(cur && cur.resumeOn && p && p.conversationId === cur.conversationId);
    };
    const block = (p, text) => setResume((r) => ({ ...r, id: p.conversationId, opening: false, asking: false, blocked: text || null }));
    // (only the answer to this page's own open restores anything: a chat read back again on a reconnect — the gate's ai:resumed on a
    // message the reader just sent under their chips — must not put older chips back over theirs: review R3)
    const onResumed = (p) => {
      if (!mine(p, true)) return;
      const cur = resumeCtxRef.current;
      // (…but D18's day line is said whichever read it came with — review 2 F2)
      if (!cur.opening) {
        if (p.notice) setResume((r) => ({ ...r, id: p.conversationId, notice: r.notice || p.notice }));
        return;
      }
      // (a chat opened AGAIN keeps what the reader has — chips and mode: RSM6)
      if (!cur.resume.reopen) {
        if (p.chips) cur.picker.restore(p.chips);
        if (typeof p.mode === "string" && p.mode) cur.setMode(p.mode);
      }
      // (a take-over answered with nothing asked here yet: the chat's history is read again, so the other tab's turns since this tab
      // first read it appear above — nothing here to draw twice; only while it is the chat on screen — a reader who opened another one
      // meanwhile stays there, and going back reads it then: review 9)
      if (cur.takingOver && cur.liveEmpty && cur.history.viewingId === p.conversationId) cur.history.open(p.conversationId, "", { everyone: true });
      setResume((r) => ({ ...r, id: p.conversationId, opening: false, asking: false, takeOver: false, blocked: null, notice: r.notice || p.notice || null,
        reopen: false, tries: 0, retryWait: false }));
    };
    const onMoved = (p) => {
      if (mine(p)) block(p, p.message);
    };
    const onLimit = (p) => {
      if (!mine(p)) return;
      if (p.left > 0) setResume((r) => ({ ...r, id: p.conversationId, limitText: p.message || null }));
      else block(p, p.message);
    };
    const onError = (p) => {
      if (!mine(p, true) || p.code === "session_expired") return;
      const cur = resumeCtxRef.current;
      // (the question this refusal is for, if it is for one — its turns are not drawn while it waits to be sent: RSM6, Q2 and P4)
      const q = cur.asked;
      // (…and the chat is then opened as AGAIN: the question goes under the chips it was asked with, none restored over them)
      const holdQuestion = (r) => (q ? { hide: [...r.hide, ...q.ids], pending: q.text, reopen: true } : {});
      // (an open refused for a passing reason keeps the answer the SDK stamped it on as it was — the first stamp's, never a later one's)
      const keepStamp = (r) => {
        const a = cur.lastAnswer;
        return a && !Object.prototype.hasOwnProperty.call(r.stamps, a.id) ? { ...r.stamps, [a.id]: { error: a.error, done: a.done } } : r.stamps;
      };
      // (an open refused for any other reason never leaves the tab waiting: a take-over refused because the other tab is answering
      // asks again; a plain open refused as in flight is this tab's own earlier open still reading the chat back — its answer is
      // awaited, never read as another tab's (review 2 F3))
      // (…and either refusal of an OPEN keeps the answer the SDK stamped it on as it was — a chat opened again has answers: review 1
      // B6, B4)
      const openStamp = (r) => (cur.opening && !q ? { stamps: keepStamp(r) } : {});
      if (p.code === "chat_open_elsewhere" || (p.code === "conversation_in_flight" && cur.takingOver)) {
        setResume((r) => {
          const here = _resumeFor(r, p.conversationId);
          return { ...here, opening: false, retryWait: false, asking: true, ...holdQuestion(here), ...openStamp(here) };
        });
      } else if (p.code === "conversation_in_flight" && cur.opening) {
        setResume((r) => {
          const here = _resumeFor(r, p.conversationId);
          return { ...here, ...openStamp(here) };
        });
      }
      else if (p.code === "chat_moved" || p.code === "chat_full") block(p, p.message);
      else if (p.code === "start_new_chat" && cur.unansweredHere && (q || (cur.opening && cur.resume.reopen))) {
        // R3-3b: a chat started here whose first answer was never saved — a NEW chat, her question in its text box and her chips on
        // it, nothing sent for her; unless she has opened another saved chat meanwhile, which stays on screen.
        if (cur.viewingSaved) return;
        const question = cur.resume.pending || (q && q.text) || cur.lastAsked;
        cur.picker.carryToNextChat();
        cur.startNewChat(null);
        if (question) cur.setDraft(question);
      } else if (p.code === "start_new_chat" && !cur.ours) {
        cur.history.close();
        cur.startNewChat(null);
      } else if (q && (p.code === "unknown_conversation" || p.code === "start_new_chat")) {
        // P4: a question the server could not place — it lost the chat (an outage of its store, or its save still behind the answer) —
        // opens the chat again and is sent once it is open; never "This chat has ended"
        setResume((r) => ({ ..._resumeFor(r, p.conversationId), ...holdQuestion(_resumeFor(r, p.conversationId)), opening: true, reopen: true, tries: 0, retryWait: false }));
      } else if (cur.opening && !q) {
        // P4: an open refused for any reason the rules above do not own — a loader, the open sent again after OPEN_RETRY_MS; after the
        // last the page stops waiting: the text box simply there, nothing said, a waiting question put back in it
        const r0 = cur.resume;
        if (r0.tries >= OPEN_RETRY_MS.length) {
          if (r0.pending) cur.setDraft(r0.pending);
          setResume((r) => ({ ..._resumeFor(r, p.conversationId), opening: false, retryWait: false, reopen: false, tries: 0, pending: null, stamps: keepStamp(_resumeFor(r, p.conversationId)) }));
        } else {
          setResume((r) => ({ ..._resumeFor(r, p.conversationId), opening: false, retryWait: true, tries: r0.tries + 1, stamps: keepStamp(_resumeFor(r, p.conversationId)) }));
        }
      }
    };
    // A dropped connection ends the turn it cut off — the server stops answering it — so its spinner is stopped here (RSM6).
    const onDropped = () => {
      openSentRef.current = null;
      const cur = resumeCtxRef.current;
      if (cur && cur.resumeOn && cur.inFlight && cur.lastId) {
        setResume((r) => {
          const here = _resumeFor(r, cur.conversationId);
          return here.cuts.includes(cur.lastId) ? here : { ...here, cuts: [...here.cuts, cur.lastId] };
        });
      }
    };
    socket.on("ai:resumed", onResumed);
    socket.on("ai:chat_moved", onMoved);
    socket.on("ai:chat_limit", onLimit);
    socket.on("ai:error", onError);
    socket.on("disconnect", onDropped);
    return () => {
      socket.off("ai:resumed", onResumed);
      socket.off("ai:chat_moved", onMoved);
      socket.off("ai:chat_limit", onLimit);
      socket.off("ai:error", onError);
      socket.off("disconnect", onDropped);
    };
  }, [socket]);
  // Whether the text box is gone for this chat: a question to answer first, or the server (or this tab) has made it read-only.
  const composerGone = resumeHere && (resume.asking || Boolean(resume.blocked));

  // The listing only changes when a turn lands in the store, so the refetch is asked for HERE, on
  // the in_flight → settled edge, rather than on a timer. reloadSoon owns the delay — the events
  // travel engine → broker → HTTP → Mongo after the turn ends, so an immediate refetch would
  // reliably miss the turn that triggered it.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !isInFlight) history.reloadSoon();
    wasInFlight.current = isInFlight;
  }, [isInFlight, history]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // An expired session is the server's one signal ({ status: 401, code: "session_expired" }, contract ErrorEvent), at connect or
  // on a turn: this page answers it as services/api.js answers an HTTP 401 — signed out, back to its own sign-in page — rather
  // than the "unavailable" screen a connect refusal otherwise draws or a refused turn's line in the thread.
  const sessionExpired = Boolean(connectionError && connectionError.code === "session_expired");
  useEffect(() => {
    if (sessionExpired) handleLogout();
    // eslint-disable-next-line
  }, [sessionExpired]);
  useEffect(() => {
    if (!socket) return undefined;
    const onExpired = (p) => {
      if (p && p.code === "session_expired") handleLogout();
    };
    socket.on("ai:error", onExpired);
    return () => socket.off("ai:error", onExpired);
    // eslint-disable-next-line
  }, [socket]);

  // Scope changes invalidate the conversation context. The reconnect that
  // env/conf changes trigger (inside SqAiProvider) also drops the server's
  // in-memory conversation state, so a fresh conversationId is required. The
  // "started a new chat" notice only shows when a conversation was underway.
  // Mode is locked in server-side on the first turn of a conversation
  // (socket.js records state.mode and follow-ups ignore the payload's), so
  // changing it mid-thread would silently do nothing — rotate the conversation.
  // A continued chat (RSM4) is a conversation underway too, and its history goes with it (D18: a scope change starts a new chat).
  const rotate = (noticeText) => {
    if (continuing) history.close();
    startNewChat(hasMessages || continuing ? noticeText : null);
  };
  const onModeChange = (next) => {
    if (next === mode) return;
    setMode(next);
    rotate("Mode changed — started a new chat.");
  };
  const onEnvChange = (next) => {
    if (next === env) return;
    setEnv(next);
    rotate("Environment changed — started a new chat.");
  };
  const onTenantChange = (next) => {
    if (next === tenantSchema) return;
    setTenantSchema(next);
    rotate("Tenant changed — started a new chat.");
  };
  const onConfidentialChange = (next) => {
    setConfidentialAccess(next);
    rotate("Access settings changed — started a new chat.");
  };
  const onWebSearchChange = (next) => {
    if (next === webSearch) return;
    setWebSearch(next);
    rotate("Web search setting changed — started a new chat.");
  };
  const onUserTzChange = (next) => {
    if (next === userTz) return;
    setUserTz(next);
    rotate("Timezone changed — started a new chat.");
  };

  // #2a: when the user asks to COMPARE a second module ("compare X and Y"), the
  // backend broadens the scope and emits ai:context_update so the new chip appears
  // in the picker (the user sees the change). Handled here where usePicker lives.
  // A ref keeps the listener stable without re-subscribing every picker change.
  const ctxUpdateRef = useRef({ picker, conversationId });
  ctxUpdateRef.current = { picker, conversationId };
  useEffect(() => {
    if (!socket) return undefined;
    const onContextUpdate = (p) => {
      const cur = ctxUpdateRef.current;
      if (!p || p.conversationId !== cur.conversationId) return;
      if (p.action === "add" && Array.isArray(p.modules)) {
        // Atomic multi-add (one setSelected) so a 2+ module payload can't drop chips.
        cur.picker.addModules(p.modules.map((m) => m && m.canonical).filter(Boolean));
        if (p.note) setNotice(p.note); // surface the backend's "Added X — comparing…" line
      } else if (p.action === "replace" && Array.isArray(p.modules)) {
        // A honoured "Switch to <module>": the pinned chip(s) come off and the typed one goes on, so
        // the next question about it meets no gate. removeModule drops the chip's templates with it.
        for (const m of (Array.isArray(p.remove) ? p.remove : [])) if (m && m.canonical) cur.picker.removeModule(m.canonical);
        cur.picker.addModules(p.modules.map((m) => m && m.canonical).filter(Boolean));
        if (p.note) setNotice(p.note);
      } else if (p.action === "date" && p.range && cur.picker.date) {
        // #2c: typed → sticky writeback. The engine resolved a typed date range this turn and
        // is pushing it into the picker as the standing filter (so it carries to later turns).
        cur.picker.date.setFromEngine(p.range);
        if (p.note) setNotice(p.note);
      }
    };
    socket.on("ai:context_update", onContextUpdate);
    return () => socket.off("ai:context_update", onContextUpdate);
  }, [socket, conversationId, setNotice]);

  // The server's line for this page alone, e.g. a suspended account's warning; it comes with every turn it applies to. ACCESS-54
  useEffect(() => {
    if (!socket) return undefined;
    const onNotice = (p) => {
      if (p && p.message && p.conversationId === ctxUpdateRef.current.conversationId) setNotice(p.message);
    };
    socket.on("ai:notice", onNotice);
    return () => socket.off("ai:notice", onNotice);
  }, [socket, setNotice]);

  // A question typed on the "This chat isn't available to you." screen (or retried from a saved chat that cannot be continued) starts
  // a NEW chat with it (RSM10). The send waits one tick: the SDK resets the thread for a new conversation id in an effect that runs
  // after this component's, and a send before it would be wiped with the thread.
  const sendRef = useRef(send);
  sendRef.current = send;
  const askInNewChat = (text) => {
    history.close();
    startNewChat(null);
    setTimeout(() => sendRef.current(text), 0);
  };
  const handleSend = (text) => {
    setNotice(null);
    if (viewingSaved && history.viewUnavailable) return askInNewChat(text);
    return send(text);
  };
  // "Ask again" under an answer that was interrupted: in the chat being continued it asks in that chat; in a saved chat that cannot be
  // continued it starts a new one with the question; another user's chat offers none (the question is theirs, not the reader's).
  const retryTurn = (text) => (continuing ? handleSend(text) : askInNewChat(text));
  const canRetry = continuing || (viewingSaved && !(history.viewOwner && history.viewOwner.self === false));

  // (with continuing on — or not yet said — a passing problem is the loader on the chat, never this screen: RSM6, P4 and P5)
  if (connectionError && !(transportDown && picker.resumeChats !== false)) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          <div className={styles.emptyMark}>
            <AlertTriangle className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <h2 className={styles.emptyTitle}>SQ-AI is unavailable</h2>
          <p className={styles.emptySub}>{connectionError.message}</p>
          <p className={styles.settingsHint}>{connectionError.code}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.column}>
          <div className={styles.headerInner}>
            <div className={styles.brand}>
              <span className={styles.brandMark} aria-label="SafetyQube">SQ</span>
              <span className={styles.brandName}>SafetyQube AI</span>
            </div>
            <div className={styles.headerControls}>
              <span
                className={`${styles.statusDot} ${connected ? styles.statusDotOn : ""}`}
                title={connected ? "Connected" : "Connecting…"}
              />
              <span className={styles.scopeText}>
                {env}
                {tenantSchema ? ` · ${tenantSchema}` : ""}
              </span>
              {/* What the SERVER said this session may see, never worked out here — a header that
                  derived it would be a second copy of the rule that filters the rows. Rendered
                  only once the server has said, so it can state nothing rather than guess. */}
              {sessionPosture && sessionPosture.accessScope === "super_admin" ? (
                <span
                  className={styles.accessChip}
                  title="This sign-in sees every module and every org unit, and drafts as a business super admin: other people's drafts only when you ask for everyone's, and never another person's draft permit. Confidential records are separate — they appear only while Settings › Include confidential records is ticked."
                >
                  super admin
                </span>
              ) : null}
              <button
                type="button"
                className={`${styles.ghostBtn} ${railOpen ? styles.ghostBtnActive : ""}`}
                onClick={() => setRailOpen((v) => !v)}
                title="Saved chats"
                aria-pressed={railOpen}
              >
                <History className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>Chats</span>
              </button>
              <button
                type="button"
                className={`${styles.ghostBtn} ${settingsOpen ? styles.ghostBtnActive : ""}`}
                onClick={() => setSettingsOpen((v) => !v)}
                title="Scope settings"
                aria-pressed={settingsOpen}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>Settings</span>
              </button>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => {
                  history.close();
                  startNewChat(null);
                }}
                disabled={isInFlight || (!hasMessages && !viewingSaved && !continuing)}
                title="New chat"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>New chat</span>
              </button>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={handleLogout}
                title="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
                <span className={styles.btnLabel}>Sign out</span>
              </button>
            </div>
          </div>
        </div>
        {settingsOpen && (
          <SettingsBar
            mode={mode}
            onModeChange={onModeChange}
            env={env}
            onEnvChange={onEnvChange}
            tenants={tenants}
            tenantSchema={tenantSchema}
            onTenantChange={onTenantChange}
            tenantsLoading={tenantsLoading}
            tenantsError={tenantsError}
            confidentialAccess={confidentialAccess}
            onConfidentialChange={onConfidentialChange}
            webSearch={webSearch}
            onWebSearchChange={onWebSearchChange}
            // The CEILING for the mode this conversation is in, as the SERVER reported it at
            // connect (ai:session). The checkbox can only narrow it, so when it is false the
            // control is disabled with the reason on it rather than offering a tick that would
            // do nothing — which is what the page would be doing if it derived this itself.
            webSearchCeiling={
              sessionPosture && sessionPosture.webSearchCeiling
                ? sessionPosture.webSearchCeiling[mode === "chart" ? "chart" : mode === "summarise" ? "summarise" : "assistant"] === true
                : null
            }
            userTz={userTz}
            onUserTzChange={onUserTzChange}
            disabled={isInFlight}
          />
        )}
      </header>

      <div className={styles.body}>
        {railOpen && (
          <HistoryRail
            conversations={history.list}
            loading={history.listLoading}
            error={history.listError}
            activeId={history.viewingId || conversationId}
            liveConversationId={conversationId}
            liveHasMessages={hasMessages}
            // Selecting the live conversation is a way BACK to it, not a read-only view of it: the
            // live thread holds this turn's charts and follow-ups, and the store holds neither.
            // (Continuing chats on: a saved chat is read with whose it is, and the caller's own continues — RSM4. The chat being
            // continued is already on screen.)
            onOpen={(id, title) => {
              if (continuing && id === conversationId) return undefined;
              setAddressConversation(id, env, tenantSchema);
              // (back to a continued chat from another one: its history above the live thread as it stood — takenRef, reviews R4 → 8)
              if (id === conversationId && resumeHere && resume.continued) {
                if (hasMessages && takenRef.current && takenRef.current.id === id) return history.restore(takenRef.current);
                return history.open(id, title, { everyone: true });
              }
              if (id === conversationId) return history.close();
              return resumeOn || !mineOnly ? history.open(id, title, { everyone: true }) : history.open(id, title);
            }}
            onNewChat={() => {
              history.close();
              startNewChat(null);
            }}
            newChatDisabled={isInFlight}
            hasMore={history.hasMore}
            loadingMore={history.loadingMore}
            onLoadMore={history.loadMore}
            loadMoreFailed={history.moreFailed}
            zone={userTz}
            resumeOn={resumeOn}
            mineBox
            mineOnly={mineOnly}
            onMineOnlyChange={setMineOnly}
          />
        )}

        {/* Fixed to the right edge, outside the workspace grid, so the conversation lays out
            identically whether it is shown or hidden. */}
        <BillingDebugPanel env={env} tenantSchema={tenantSchema} zone={userTz} />

        <div className={styles.workspace}>
          <main className={styles.scroll} ref={scrollRef}>
            {/* Until the server has said whether continuing is on, only a loader (P5 — RSM6): nothing it might draw wrongly first. */}
            {picker.resumeChats === null ? (
              <div className={styles.columnFill}>
                <div className={styles.resumeLoader} role="status" aria-label="Loading chat" />
              </div>
            ) : viewingSaved ? (
              <div className={styles.column}>
                <HistoryTranscript
                  // Remounts per conversation, so "open at the newest message" runs for each one
                  // rather than only for the first opened.
                  key={history.viewingId}
                  messages={history.viewMessages}
                  loading={history.viewLoading}
                  error={history.viewError}
                  hasEarlier={history.hasEarlier}
                  loadingEarlier={history.loadingEarlier}
                  onLoadEarlier={history.loadEarlier}
                  earlierFailed={history.earlierFailed}
                  onRetry={canRetry && !isInFlight ? retryTurn : undefined}
                  userTz={userTz}
                  // (continuing chats on: the caller's own is about to continue, so no "start a new chat" note; another user's
                  // says whose it is and stays read-only — RSM4, D21)
                  continuing={resumeOn && !(history.viewOwner && history.viewOwner.self === false)}
                  readOnlyLabel={history.viewOwner && history.viewOwner.self === false ? `${ownerLabel(history.viewOwner)}'s chat — read-only` : null}
                />
              </div>
            ) : continuing || hasMessages ? (
              <div className={styles.column}>
                {continuing && (
                  <HistoryTranscript
                    key={history.viewingId}
                    messages={history.viewMessages}
                    loading={history.viewLoading}
                    error={history.viewError}
                    hasEarlier={history.hasEarlier}
                    loadingEarlier={history.loadingEarlier}
                    onLoadEarlier={history.loadEarlier}
                    earlierFailed={history.earlierFailed}
                    onRetry={retryTurn}
                    userTz={userTz}
                    continuing
                  />
                )}
                {resumeHere && resume.notice && <p className={styles.notice}>{resume.notice}</p>}
                {notice && <p className={styles.notice}>{notice}</p>}
                {hasMessages && (
                  <Thread
                    messages={messages}
                    onSuggestionClick={handleSend}
                    suggestionsDisabled={isInFlight || !connected || !tenantSchema || !tenantsReady || composerGone || (resumeHere && (resume.opening || resume.retryWait))}
                    env={env}
                    tenantSchema={tenantSchema}
                    scrollRef={scrollRef}
                    times={times}
                    userTz={userTz}
                  />
                )}
                {resumeHere && resume.limitText && !resume.blocked && <p className={styles.notice}>{resume.limitText}</p>}
                {resumeHere && resume.blocked && <p className={styles.notice}>{resume.blocked}</p>}
                {(connectionLost || waitingHere) && <div className={styles.resumeLoader} role="status" aria-label="Loading chat" />}
                {resumeHere && resume.asking && (
                  <div className={styles.resumeAsk} role="dialog" aria-label="Continue here?">
                    <span>This chat is already open in another tab. Continue here?</span>
                    <button
                      type="button"
                      className={styles.ghostBtn}
                      onClick={() => setResume((r) => ({ ...r, asking: false, opening: true, takeOver: true }))}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={styles.ghostBtn}
                      onClick={() => setResume((r) => ({ ...r, asking: false, pending: null, blocked: "This chat is open in another tab — read-only here." }))}
                    >
                      No
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.columnFill}>
                {notice && <p className={styles.notice}>{notice}</p>}
                <EmptyState onSelectExample={handleSend} disabled={!connected || !tenantSchema || !tenantsReady} terms={terms} />
                {connectionLost && <div className={styles.resumeLoader} role="status" aria-label="Loading chat" />}
              </div>
            )}
          </main>

          {/* ABSENT rather than disabled while a saved chat is open. The composer sends into the
              LIVE conversation, so leaving it on screen under a saved transcript would offer to
              continue a chat that cannot be continued, and would answer somewhere the reader is
              not looking. Going back restores it with the live thread underneath. The exception is a chat that is not there or not the reader's
              (RSM10): the screen says so and keeps the text box, and typing starts a new chat. */}
          {/* …and with continuing chats on, absent while "Continue here?" waits for an answer and once the chat is read-only here
              (moved to another tab, full, or answered no); waiting while the server reads the chat back. */}
          {(!viewingSaved || history.viewUnavailable) && !composerGone && picker.resumeChats !== null && (
            <footer>
              <div className={styles.column}>
                <Composer
                  ref={composerRef}
                  onSubmit={handleSend}
                  onCancel={cancel}
                  isInFlight={isInFlight}
                  disabled={!connected || !tenantSchema || !tenantsReady || (resumeHere && (resume.opening || resume.retryWait))}
                  note={picker.note}
                  onDismissNote={picker.dismissNote}
                  picker={
                    <ContextPicker
                      available={picker.available}
                      availableLoading={picker.availableLoading}
                      availableError={picker.availableError}
                      selected={picker.selected}
                      templatesFor={picker.templatesFor}
                      toggleModule={picker.toggleModule}
                      removeModule={picker.removeModule}
                      onModuleTemplatesChange={picker.setModuleTemplates}
                      loadTemplates={picker.loadTemplates}
                      getTemplateEntry={picker.getTemplateEntry}
                      org={picker.org}
                      date={picker.date}
                      disabled={!connected || !tenantSchema}
                    />
                  }
                />
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SqAiPage() {
  // Sandbox-local state. Deliberately NOT persisted to the same localStorage
  // keys the chart-agent dev UI used, so toggling here doesn't affect other
  // surfaces. Defaults: prod env (the CEO wants real data), confidential off
  // (fail-closed).
  // Output discipline for the conversation. "general" is the sandbox default
  // (the developer's ask, 13 Oct); "chart" and "summarise" are the other two the engine
  // whitelists. Sticky per conversation — the page rotates conversationId on change.
  const [mode, setMode] = useState("general");
  const [addressScope] = useState(readAddressScope);
  const [env, setEnv] = useState(addressScope.env || "prod");
  const [tenants, setTenants] = useState([]);
  const [tenantSchema, setTenantSchema] = useState(addressScope.tenant || "");
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);
  // The env whose tenant list has arrived. Until it is the env on screen nothing is sent: the tenant still names the PREVIOUS env's
  // account, which the list then replaces without starting a new chat, so a question sent meanwhile was asked under one account and
  // continued under another (RSM5 — found by RSM4's review, pre-existing, the switch on or off).
  const [tenantsEnv, setTenantsEnv] = useState(null);
  const [confidentialAccess, setConfidentialAccess] = useState(false);
  // Whether this session ASKS for web search. `true` is "no opinion" and not "force on": the
  // engine's ceiling (webSearchAccess.js, environment today) decides, and this value may only
  // narrow it — so the default leaves the ceiling's answer intact and unticking is a real
  // per-session off switch. Ticking it can never turn search on where the ceiling has it off.
  const [webSearch, setWebSearch] = useState(true);
  // Querying user's IANA timezone (sandbox dropdown). The agent counts every date in this zone and
  // names it in the reply, matching the dashboard; blank ("Not set") counts in UTC, as a profile with no
  // zone does. A customer's zone is their profile in the database, never this and never a token claim (K-RB8-2).
  const [userTz, setUserTz] = useState("Asia/Calcutta");

  const [conversationId, setConversationId] = useState(() => newConversationId());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  // Tenant list. Reuses tenantApi (the same source the chart-agent dev UI used)
  // so the available-tenants set is consistent across surfaces.
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    setTenantsError(null);
    tenantApi
      .getAllForEnv(env)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data.tenants || [];
        setTenants(list);
        setTenantsLoading(false);
        const stillValid = tenantSchema && list.some((t) => (t.name || t.schema || t) === tenantSchema);
        if (!stillValid) setTenantSchema(list[0] ? list[0].name || list[0].schema || list[0] : "");
        setTenantsEnv(env);
      })
      .catch((err) => {
        if (cancelled) return;
        setTenantsError(err.message || "Could not load tenants");
        setTenantsLoading(false);
        // (no list replaces the tenant, so the one on screen is the one sent)
        setTenantsEnv(env);
      });
    return () => {
      cancelled = true;
    };
    // tenantSchema deliberately excluded — we only refetch when env changes.
    // eslint-disable-next-line
  }, [env]);

  // Tenant terminology for the empty-state chips. Cached per (env, tenant)
  // for the session; a failed fetch just leaves `terms` null and the chips
  // fall back to generic wording — the empty state never blocks on this.
  const termsCacheRef = useRef(new Map());
  const [terms, setTerms] = useState(null);
  useEffect(() => {
    if (!tenantSchema) {
      setTerms(null);
      return;
    }
    const key = `${env}:${tenantSchema}`;
    const cached = termsCacheRef.current.get(key);
    if (cached) {
      setTerms(cached);
      return;
    }
    let cancelled = false;
    setTerms(null);
    aiAssistantApi
      .getTenantTerminology(env, tenantSchema)
      .then((res) => {
        if (cancelled) return;
        const t = res.data && res.data.terms;
        if (t) {
          termsCacheRef.current.set(key, t);
          setTerms(t);
        }
      })
      .catch(() => {
        /* generic chips are the fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [env, tenantSchema]);

  // Context picker scope (modules + templates the user pins). Lives at the
  // chat-session level so it persists across turns; resetKey=conversationId
  // clears it on a new chat or any scope change (which rotates conversationId).
  const picker = usePicker({ env, tenantSchema, resetKey: conversationId });

  // Saved chats. Keyed on the tenant alone, because the tenant names the Mongo collection the read
  // opens — it is NOT reset by conversationId the way the picker is: the point of the rail is to
  // outlive the conversation on screen.
  // "Mine only" (RSM4, D21 — on this page whatever the resume switch says): ticked by default, the caller's own chats as before;
  // unticked, every internal user's chats.
  const [mineOnly, setMineOnly] = useState(true);
  const history = useChatHistory({ tenantSchema, everyone: !mineOnly });

  const startNewChat = useCallback((noticeText) => {
    setAddressConversation(null);
    setConversationId(newConversationId());
    setNotice(noticeText || null);
  }, []);
  // A saved chat of the caller's own taken up as the live conversation (RSM4): its id becomes the conversation's, so the SDK and the
  // picker start it afresh and its first message continues it on the server.
  const continueChat = useCallback((id) => {
    setConversationId(id);
    setNotice(null);
  }, []);

  return (
    <SqAiProvider
      conversationId={conversationId}
      tenantSchema={tenantSchema}
      context={picker.context}
      mode={mode}
      auth={{ env, confidentialAccess, userTz, webSearch }}
      uiMode="prod"
      // The generated-SQL accordion is a DEVELOPMENT affordance and product asked for it off
      // (26 Aug call). This is the only surface that ever showed it: the webapp already pins
      // devSql false on both its AI entry points, and server/middleware/sqToken.js pins it false
      // for every SafetyQube-token session. Pinned off HERE too, so the SQL never crosses the
      // socket rather than being hidden after it arrives. Flipping it back is this one line.
      devSql={false}
    >
      <SqAiConsole
        mode={mode}
        setMode={setMode}
        env={env}
        tenants={tenants}
        tenantSchema={tenantSchema}
        tenantsLoading={tenantsLoading}
        tenantsError={tenantsError}
        tenantsReady={tenantsEnv === env}
        confidentialAccess={confidentialAccess}
        webSearch={webSearch}
        userTz={userTz}
        conversationId={conversationId}
        settingsOpen={settingsOpen}
        setSettingsOpen={setSettingsOpen}
        notice={notice}
        setNotice={setNotice}
        terms={terms}
        picker={picker}
        history={history}
        setEnv={setEnv}
        setTenantSchema={setTenantSchema}
        setConfidentialAccess={setConfidentialAccess}
        setWebSearch={setWebSearch}
        setUserTz={setUserTz}
        startNewChat={startNewChat}
        continueChat={continueChat}
        mineOnly={mineOnly}
        setMineOnly={setMineOnly}
      />
    </SqAiProvider>
  );
}
